import { CircleCheck, CloudOff, Clock3, LocateOff, TriangleAlert, X } from 'lucide-react'
import type { DetectionSnapshot } from '@/features/detection/application/DetectionRunner'
import type { DetectionProfile } from '@/features/detection/domain/profile'
import type { SuspendReason } from '@/features/detection/domain/types'
import { describeEvent, formatDuration as formatClock } from '@/features/detection/presentation/describeEvent'
import { decisionKey, latestDecision } from '../focus'

// Bandeaux du panneau gauche (maquettes) : décision récente du moteur, détection suspendue, synchronisation en
// attente, hors ligne. Jamais de fenêtre bloquante. Le mode secours (lot O6) n'est pas encore proposé.

function suspendedText(reason: SuspendReason | null, accuracyM: number | null, profile: DetectionProfile): string {
  switch (reason) {
    case 'INACCURATE':
      return accuracyM === null
        ? 'Précision inconnue.'
        : `Précision ± ${String(Math.round(accuracyM))} m, au-delà du seuil admis (${String(profile.maxAccuracyM)} m).`
    case 'STALE':
      return 'Position trop ancienne.'
    case 'JUMP':
      return 'Position incohérente avec la précédente.'
    case 'SIGNAL_LOST':
      return 'Signal GPS perdu.'
    case 'CLOCK_ANOMALY':
      return 'Heure de la tablette modifiée.'
    case null:
      return ''
  }
}

export interface FieldBannersProps {
  snapshot: DetectionSnapshot | null
  profile: DetectionProfile | null
  observing: boolean
  labelOf: (itemId: string) => string
  dismissed: string | null
  onDismiss: (key: string) => void
  online: boolean
  pendingPoints: number
}

export function FieldBanners({
  snapshot,
  profile,
  observing,
  labelOf,
  dismissed,
  onDismiss,
  online,
  pendingPoints,
}: FieldBannersProps) {
  const decision = latestDecision(snapshot)
  const showDecision = decision && profile && decisionKey(decision) !== dismissed
  const suspended = snapshot?.view.gps.status === 'SUSPENDED' && profile

  return (
    <div className="op-banners">
      {showDecision ? (
        <div
          className={`op-banner2 ${decision.kind === 'COMPLETED' ? 'op-banner2--done' : 'op-banner2--warn'}`}
          role="status"
        >
          {decision.kind === 'COMPLETED' ? <CircleCheck aria-hidden="true" /> : <TriangleAlert aria-hidden="true" />}
          <div>
            <strong>
              {decision.kind === 'COMPLETED'
                ? observing
                  ? `Observation : ${labelOf(decision.itemId)} aurait été terminée`
                  : `${labelOf(decision.itemId)} terminée`
                : observing
                  ? `Observation : ${labelOf(decision.itemId)} n’aurait pas été terminée`
                  : `${labelOf(decision.itemId)} revient à faire`}
            </strong>
            <span>
              {decision.kind === 'COMPLETED'
                ? `${formatClock(decision.observedMs)} observé · ${
                    decision.trigger === 'DEPARTURE' ? 'départ confirmé' : 'entrée suivante confirmée'
                  }`
                : describeEvent(decision, labelOf, profile, observing).motive}
              {observing ? '. Le travail se confirme par le maintien de 3 s.' : ''}
            </span>
          </div>
          <button
            type="button"
            className="op-banner2__close"
            aria-label="Fermer ce message"
            onClick={() => {
              onDismiss(decisionKey(decision))
            }}
          >
            <X aria-hidden="true" />
          </button>
        </div>
      ) : null}

      {suspended ? (
        <div className="op-banner2 op-banner2--warn" role="status">
          <LocateOff aria-hidden="true" />
          <div>
            <strong>Détection automatique suspendue</strong>
            <span>
              {suspendedText(snapshot.view.gps.reason, snapshot.lastFix?.accuracyM ?? null, profile)} Le temps acquis est
              conservé; rien n’est ajouté ni décidé avant le retour d’un signal fiable.
            </span>
          </div>
        </div>
      ) : null}

      {!online ? (
        <div className="op-banner2 op-banner2--error" role="status">
          <CloudOff aria-hidden="true" />
          <div>
            <strong>Serveur injoignable</strong>
            <span>
              La mission continue sur la tablette : les positions sont gardées et envoyées au retour du réseau. Les
              confirmations de travail attendent la réponse du serveur.
            </span>
          </div>
        </div>
      ) : pendingPoints > 0 ? (
        <div className="op-banner2" role="status">
          <Clock3 aria-hidden="true" />
          <div>
            <strong>Synchronisation en attente</strong>
            <span>
              {pendingPoints} position{pendingPoints > 1 ? 's' : ''} enregistrée{pendingPoints > 1 ? 's' : ''} sur la
              tablette, envoyée{pendingPoints > 1 ? 's' : ''} dès que possible.
            </span>
          </div>
        </div>
      ) : null}
    </div>
  )
}
